import { deserialize } from 'deserialize-json-api'

import {
  deepCamelizeKeys,
  deserializeEmbeddedSoapNote,
  deserializeTodos,
  fileConfig,
  mapDashboardLabs,
  mapGaps,
  mapQuarterlyAutomations,
  mapRuleSets,
  mapSettings,
  mapTaskFiles,
  mapTours,
  mapUserCard,
  mapWeeklyGaps,
} from './API.utils'
import { Service } from './index'

const API = {
  me: {
    details: (config) => Service.get('/me', config),
    consents: () => Service.get('/consents_documents'),
  },
  platform: {
    soaps: {
      configuration: {
        list: (query) => Service.get('/soap_note_configuration', query),
        id: (id) => ({
          details: () => Service.get(`/soap_note_configuration/${id}`),
        }),
      },
    },
    addresses: (query, config) => Service.get('/addresses/lookup_by_zip_code', query, config),
    businessHours: () => Service.get('/business_hours'),
    states: () => Service.get('/states'),
    features: {
      list: () => Service.get('/feature_flags'),
      id: (featureId) => ({
        details: (config) => Service.get(`/feature_flags/${featureId}`, undefined, config),
        toggle: () => Service.patch(`/feature_flags/${featureId}/toggle`),
      }),
    },
    blackouts: {
      list: (query) => Service.get('/blackout_periods', query),
      create: (data) => Service.post('/blackout_periods', data),
      id: (blackoutId) => ({
        remove: () => Service.delete(`/blackout_periods/${blackoutId}`),
      }),
    },
    notifications: {
      list: (query) => Service.get('/platform_notifications', query),
      create: (data) => Service.post('/platform_notifications', data),
      id: (notificationId) => ({
        update: (data) => Service.put(`/platform_notifications/${notificationId}`, data),
      }),
    },
    constants: {
      list: () => Service.get('/dropdown_options'),
      id: (key) => ({
        details: (config) => Service.get(`/dropdown_options/${key}`, undefined, config),
      }),
    },
    encounterTypes: {
      list: (query) => Service.get(`/encounter_types`, query),
      id: (encounterTypeId) => ({
        details: (query) => Service.get(`/encounter_types/${encounterTypeId}`, query),
        bookingConfig: (query) => Service.get(`/encounter_types/${encounterTypeId}/booking_config`, query),
      }),
    },
    providerTypes: {
      list: () => Service.get('/provider_types'),
    },
    settings: {
      list: () => Service.get('/system_settings').then(mapSettings),
    },
  },
  theme: {
    details: (query, config) => Service.get('/theme', query, config),
  },
  heartbeat: {
    // Best-effort session keep-alive: transient network failures here are client connectivity
    // noise, so opt out of Honeybadger reporting by default (callers can override via config).
    check: (query, config = {}) =>
      Service.get(`${import.meta.env.VITE_API_URL.replace('/api', '')}/heartbeat`, query, {
        headers: { Accept: 'application/json' },
        skipReporting: true,
        ...config,
      }),
    keep: (data, config = {}) =>
      Service.post(`${import.meta.env.VITE_API_URL.replace('/api', '')}/heartbeat`, data, {
        headers: { Accept: 'application/json' },
        skipReporting: true,
        ...config,
      }),
  },
  auth: {
    forgotPassword: (data) => Service.post('/auth/forgot_password_email', data),
    login: {
      check: (query, config) => Service.get('/auth/login', query, config),
      execute: (data) => Service.post('/auth/login', data),
      dob: (data, config) => Service.post('/auth/dob_login', data, config),
      as: (userId) => Service.post(`/auth/login_as?user_id=${userId}`),
    },
    code: {
      login: (data, config) => Service.post('/auth/verification_code', data, config),
      resend: (data, config) => Service.put('/auth/verification_code/resend', data, config),
      verify: (data, config) => Service.post('/auth/verification_code/patients', data, config),
      retrieve: (query, config) => Service.get('/auth/code', query, config),
    },
  },
  admin: {
    gapBoard: {
      providers: (query) => Service.get('/admin/gap_board/providers', query).then(deepCamelizeKeys),
    },
    metrics: (query) => ({
      data: () => Service.get(`/admin_metrics`, query),
      export: () => Service.get(`/admin_metrics/csv`, query, fileConfig),
    }),
    cbos: {
      list: (query) => Service.get('/admin/cbos', query),
      create: (data) => Service.post('/admin/cbos', data),
      id: (cboId) => ({
        details: (query, config) => Service.get(`/admin/cbos/${cboId}`, query, config),
        update: (data) => Service.patch(`/admin/cbos/${cboId}`, data),
        updateStatus: (data) => Service.patch(`/admin/cbos/${cboId}/update_status`, data),
        replacePharmacy: (data) => Service.patch(`/admin/cbos/${cboId}/replace_pharmacy`, data),
        contactUsers: {
          list: (query) => Service.get(`/admin/cbos/${cboId}/contact_users`, query),
        },
        providers: {
          list: (query) => Service.get(`/admin/cbos/${cboId}/providers`, query),
          add: (data) => Service.post(`/admin/cbos/${cboId}/providers`, data),
          id: (providerId) => ({
            update: (data) => Service.put(`/admin/cbos/${cboId}/providers/${providerId}`, data),
            remove: () => Service.delete(`/admin/cbos/${cboId}/providers/${providerId}`),
          }),
        },
      }),
    },
    providers: {
      list: (query) => Service.get('/admin/providers', query),
      cpas: (query) => Service.get('/admin/providers/cpas', query),
      id: (providerId) => ({
        details: () => Service.get(`/admin/providers/${providerId}`),
        notes: {
          list: (query) => Service.get(`/admin/providers/${providerId}/notes`, query),
        },
        supervisedProviders: {
          list: (query) => Service.get(`/admin/providers/${providerId}/supervised_providers`, query),
        },
        cbos: {
          list: (query) => Service.get(`/admin/providers/${providerId}/cbos`, query),
          join: (data) => Service.post(`/admin/providers/${providerId}/cbos`, data),
          id: (cboId) => ({
            leave: (data) => Service.delete(`/admin/providers/${providerId}/cbos/${cboId}`, data),
            update: (data) => Service.put(`/admin/providers/${providerId}/cbos/${cboId}`, data),
          }),
        },
      }),
    },
    systemUsers: {
      list: (query) => Service.get('/admin/system_users', query),
      create: (data) => Service.post('/admin/system_users', data),
      id: (userId) => ({
        details: () => Service.get(`/admin/system_users/${userId}`),
        update: (data) => Service.patch(`/admin/system_users/${userId}`, data),
      }),
    },
  },
  patients: {
    list: (query) => Service.get('/patients', query),
    create: (data) => Service.post('/patients', data),
    healthHistoryAlerts: (query) => Service.get('/patients_with_health_history_alerts', query),
    enrollment: {
      create: (data) => Service.post('/patients/enrollments', data),
      update: (data, config = {}) =>
        Service.patch('/patients/enrollments', data, config).then((res) => {
          if (data.enrollment_step === 'last') return res
          return deserialize(res, { transformKeys: 'camelCase' })?.data ?? []
        }),
    },
    reports: {
      main: (query) => Service.get('/patients/reports', query),
      cboAssignment: (query) => Service.get('/patients/reports/cbo_assignment', query),
      hiv: (query) => Service.get('/patients/reports/hiv', query),
      insurance: (query) => Service.get('/patients/reports/insurance', query),
      paDisposition: (variant, query) => Service.get(`/patients/reports/pa_disposition_${variant}`, query),
      inactivePatients: (query) => Service.get('/patients/reports/inactive_patients', query),
      conditions: (query) => Service.get('/patients/reports/conditions', query),
      conditionsSummary: (query) => Service.get('/patients/reports/conditions_summary', query),
      ineligibleHivEnrollments: (query) => Service.get('/patients/reports/ineligible_hiv_enrollments', query),
      dukeDemo: (query) => Service.get('/patients/reports/duke_demo', query),
      dukeSoap: (query) => Service.get('/patients/reports/duke_soap', query),
      enrollment: (query) => Service.get('/patients/reports/enrollment', query),
    },
    id: (patientId) => ({
      details: (config) => Service.get(`/patients/${patientId}`, undefined, config),
      internalConversation: (query) => Service.get(`/patients/${patientId}/internal_conversations`, query),
      update: (data) => Service.patch(`/patients/${patientId}`, data),
      accessible: () => Service.get(`/patients/${patientId}/accessible`),
      sendWorkNote: (data) => Service.post(`/patients/${patientId}/work_notes`, data),
      sendPushNotification: (data) => Service.post(`/patients/${patientId}/push_notifications`, data),
      restartCare: (data) => Service.post(`/patients/${patientId}/re_engage`, data),
      loginLink: (data) => Service.post(`/patients/${patientId}/two_fa_url`, data),
      quickLinks: {
        create: (data) => Service.post(`/patients/${patientId}/quick_links`, data),
      },
      todos: {
        list: () => Service.get(`/patients/${patientId}/todos`).then(deserializeTodos),
        id: (todoId) => ({
          update: (data) => Service.patch(`/patients/${patientId}/todos/${todoId}`, data),
        }),
      },
      verifications: {
        send: (data) => Service.post(`/patients/${patientId}/verifications`, data),
        verify: (data, config) => Service.patch(`/patients/${patientId}/verifications`, data, config),
      },
      profilePicture: {
        create: (data) => Service.post(`/patients/${patientId}/profile_picture`, data),
        remove: () => Service.delete(`/patients/${patientId}/profile_picture`),
      },
      photoID: {
        list: () => Service.get(`/patients/${patientId}/photo_ids`),
        details: () => Service.get(`/patients/${patientId}/photo_id`),
        create: (data) => Service.post(`/patients/${patientId}/photo_id`, data),
        remove: (data) => Service.delete(`/patients/${patientId}/photo_id`, data),
      },
      schedulingEligibilities: {
        list: (query) => Service.get(`/patients/${patientId}/scheduling_eligibilities`, query),
        appointments: {
          id: (appointmentId) => ({
            details: () => Service.get(`/patients/${patientId}/scheduling_eligibilities/appointments/${appointmentId}`),
          }),
        },
        id: (encounterTypeId) => ({
          details: () => Service.get(`/patients/${patientId}/scheduling_eligibilities/${encounterTypeId}`),
        }),
      },
      availabilities: {
        list: (query) => Service.get(`/patients/${patientId}/availabilities`, query),
      },
      cbos: {
        list: (query, config) => Service.get(`/patients/${patientId}/cbos`, query, config),
        id: (cboId) => ({
          verify: (query) => Service.get(`/patients/${patientId}/cbos/${cboId}/verify_patient_cbo`, query),
        }),
      },
      providers: {
        list: (query) => Service.get(`/patients/${patientId}/providers`, query),
      },
      gilead: {
        create: (data) => Service.post(`/patients/${patientId}/gilead/applications`, data),
        expire: (data) => Service.post(`/patients/${patientId}/gilead/applications/expire`, data),
        id: (gileadApplicationId) => ({
          details: () => Service.get(`/patients/${patientId}/gilead/applications/${gileadApplicationId}`),
          update: (data) => Service.put(`/patients/${patientId}/gilead/applications/${gileadApplicationId}`, data),
          patientSign: (data) => Service.put(`/patients/${patientId}/gilead/applications/${gileadApplicationId}/sign`, data),
          providerSign: (data) => Service.put(`/patients/${patientId}/gilead/applications/${gileadApplicationId}/provider_sign`, data),
          decline: (data) => Service.post(`/patients/${patientId}/gilead/applications/${gileadApplicationId}/decline`, data),
        }),
      },
      consents: {
        create: () => Service.post(`/patients/${patientId}/send_consent`),
        list: (query) => Service.get(`/patients/${patientId}/consents`, query),
        agree: (data, config) => Service.patch(`/patients/${patientId}/consents`, data, config),
      },
      priorAuth: {
        submit: (data) => Service.post(`/patients/${patientId}/prior_auths`, data),
        preview: (data) => Service.post(`/patients/${patientId}/prior_auths/preview`, data, fileConfig),
        glp1Options: () =>
          Service.get(`/patients/${patientId}/prior_auths/glp1_options`).then(({ fields, values }) => ({
            fields: deepCamelizeKeys(fields),
            values,
          })),
      },
      breakGlass: {
        unlock: (data) => Service.post(`/patients/${patientId}/break_glass`, data),
        reset: (config) => Service.delete(`/patients/${patientId}/break_glass`, undefined, config),
      },
      events: {
        list: (query) => Service.get(`/patients/${patientId}/events`, query),
        export: () => Service.get(`/patients/${patientId}/events/export`, fileConfig),
      },
      quarterlyAutomations: {
        list: (query) => Service.get(`/patients/${patientId}/quarterly_automations`, query).then(mapQuarterlyAutomations),
      },
      labRequests: {
        create: (data) => Service.post(`/patients/${patientId}/lab_requests`, data),
        id: (labRequestId) => ({
          expire: () => Service.post(`/patients/${patientId}/lab_requests/${labRequestId}/expire`),
        }),
      },
      documents: {
        list: (query) => Service.get(`/patients/${patientId}/documents`, query),
        create: (data, config) => Service.post(`/patients/${patientId}/documents`, data, config),
        uploadNySyphRecord: () => Service.post(`/patients/${patientId}/documents/upload_ny_syph_record`),
        id: (documentId) => ({
          details: (config) => Service.get(`/patients/${patientId}/documents/${documentId}`, undefined, config),
          update: (data) => Service.patch(`/patients/${patientId}/documents/${documentId}`, data),
          remove: () => Service.delete(`/patients/${patientId}/documents/${documentId}`),
          view: () => Service.patch(`/patients/${patientId}/documents/${documentId}/mark_viewed`),
        }),
      },
      assessments: {
        list: (query) => Service.get(`/patients/${patientId}/assessments`, query),
        create: (data) => Service.post(`/patients/${patientId}/assessments`, data),
        id: (assessmentId) => ({
          details: () => Service.get(`/patients/${patientId}/assessments/${assessmentId}`),
          update: (data) => Service.patch(`/patients/${patientId}/assessments/${assessmentId}`, data),
          expire: () => Service.post(`/patients/${patientId}/assessments/${assessmentId}/expire`),
        }),
        async: {
          id: (assessmentId) => ({
            details: () => Service.get(`/patients/${patientId}/async_assessments/${assessmentId}`),
            update: (data) => Service.patch(`/patients/${patientId}/async_assessments/${assessmentId}`, data),
          }),
        },
      },
      insurances: {
        list: (query) => Service.get(`/patients/${patientId}/insurances`, query),
        create: (data) => Service.post(`/patients/${patientId}/insurances`, data),
        summary: () => Service.get(`/patients/${patientId}/insurance_summary`),
        id: (insuranceId) => ({
          details: (config) => Service.get(`/patients/${patientId}/insurances/${insuranceId}`, undefined, config),
          update: (data) => Service.patch(`/patients/${patientId}/insurances/${insuranceId}`, data),
          remove: () => Service.delete(`/patients/${patientId}/insurances/${insuranceId}`),
        }),
      },
      insuranceVerifications: {
        create: (config) => Service.post(`/patients/${patientId}/insurance_verifications`, undefined, config),
        status: (config) => Service.get(`/patients/${patientId}/insurance_verifications/status`, undefined, config),
      },
      healthHistory: {
        list: (query) => Service.get(`/patients/${patientId}/health_histories`, query),
        export: () => Service.get(`/patients/${patientId}/health_histories/export`, undefined, fileConfig),
        notes: {
          list: (query) => Service.get(`/patients/${patientId}/health_histories/notes`, query),
          create: (data) => Service.post(`/patients/${patientId}/health_histories/notes`, data),
        },
        alerts: {
          list: (query) => Service.get(`/patients/${patientId}/health_histories/alerts`, query),
          acknowledge: (alertId, data) => Service.patch(`/patients/${patientId}/health_histories/alerts/${alertId}`, data),
        },
        type: (type) => ({
          list: (query) => Service.get(`/patients/${patientId}/${type}`, query),
          create: (data) => Service.post(`/patients/${patientId}/${type}`, data),
          id: (id) => ({
            update: (data) => Service.patch(`/patients/${patientId}/${type}/${id}`, data),
            remove: () => Service.delete(`/patients/${patientId}/${type}/${id}`),
          }),
        }),
      },
      appointments: {
        list: (query) => Service.get(`/patients/${patientId}/appointments`, query),
        home: () => Service.get(`/patients/${patientId}/home/appointments`),
        create: (data) => Service.post(`/patients/${patientId}/appointments`, data),
        id: (appointmentId) => ({
          details: () => Service.get(`/patients/${patientId}/appointments/${appointmentId}`),
          update: (data) => Service.patch(`/patients/${patientId}/appointments/${appointmentId}`, data),
          cancel: () => Service.patch(`/patients/${patientId}/appointments/${appointmentId}/cancel`),
          invite: () => Service.post(`/patients/${patientId}/appointments/${appointmentId}/send_invite`),
          joinWaitingRoom: () => Service.post(`/patients/${patientId}/appointments/${appointmentId}/join_waiting_room`),
          completeVideo: () => Service.post(`/patients/${patientId}/appointments/${appointmentId}/complete_video`),
          payments: {
            check: (data) => Service.post(`/patients/${patientId}/appointments/${appointmentId}/payments`, data),
          },
          soap: {
            schema: (query) =>
              Service.get(`/patients/${patientId}/appointments/${appointmentId}/soap_notes`, query, { skipDeserialization: true })
                .then((resp) => deserialize(resp)?.data)
                .then(deserializeEmbeddedSoapNote),
            update: (data) =>
              Service.patch(`/patients/${patientId}/appointments/${appointmentId}/soap_notes`, data, { skipDeserialization: true })
                .then((resp) => deserialize(resp)?.data)
                .then(deserializeEmbeddedSoapNote),
          },
        }),
      },
      payments: {
        list: (query) => Service.get(`/patients/${patientId}/payments`, query),
        id: (paymentId) => ({
          details: () => Service.get(`/patients/${patientId}/payments/${paymentId}`),
          resolve: () => Service.patch(`/patients/${patientId}/payments/${paymentId}`),
        }),
      },
      encounterTypes: {
        id: (encounterTypeId) => ({
          payments: {
            check: (data) => Service.post(`/patients/${patientId}/encounter_types/${encounterTypeId}/payments`, data),
          },
        }),
      },
      prescriptions: {
        list: (query) => Service.get(`/patients/${patientId}/prescriptions`, query),
        id: (prescriptionId) => ({
          details: (config) => Service.get(`/patients/${patientId}/prescriptions/${prescriptionId}`, undefined, config),
        }),
      },
      labs: {
        list: (query) => Service.get(`/patients/${patientId}/labs`, query),
        home: () => Service.get(`/patients/${patientId}/home/labs`),
        order: (data) => Service.post(`/patients/${patientId}/labs`, data),
        vendors: (query) => Service.get(`/patients/${patientId}/lab_vendors`, query),
        files: {
          id: (fileId) => ({
            details: () => Service.get(`/patients/${patientId}/labs_files/${fileId}`),
            rename: (data) => Service.patch(`/patients/${patientId}/labs_files/${fileId}`, data),
          }),
        },
        id: (labId) => ({
          details: () => Service.get(`/patients/${patientId}/labs/${labId}`),
          update: (data) => Service.patch(`/patients/${patientId}/labs/${labId}`, data),
          notes: {
            list: (query) => Service.get(`/patients/${patientId}/labs/${labId}/notes`, query),
            create: (data) => Service.post(`/patients/${patientId}/labs/${labId}/notes`, data),
          },
        }),
      },
      roiMedicalRecords: {
        create: (data) => Service.post(`/patients/${patientId}/roi_medical_records`, data),
        preview: (query) => Service.get(`/patients/${patientId}/roi_medical_records/preview`, query, fileConfig),
        id: (medicalRecordId) => ({
          details: () => Service.get(`/patients/${patientId}/roi_medical_records/${medicalRecordId}`),
          fax: (data) => Service.post(`/patients/${patientId}/roi_medical_records/${medicalRecordId}/fax`, data),
        }),
      },
      roiRequests: {
        create: () => Service.post(`/patients/${patientId}/roi_requests`),
        id: (roiRequestId) => ({
          details: () => Service.get(`/patients/${patientId}/roi_requests/${roiRequestId}`),
          sign: (data) => Service.patch(`/patients/${patientId}/roi_requests/${roiRequestId}`, data),
          decline: (data) => Service.post(`/patients/${patientId}/roi_requests/${roiRequestId}/decline`, data),
        }),
      },
      notes: {
        create: (data) => Service.post(`/patients/${patientId}/notes`, data),
        list: (query) => Service.get(`/patients/${patientId}/notes`, query),
      },
      priorityNotes: {
        list: (query) => Service.get(`/patients/${patientId}/priority_notes`, query),
        create: (data) => Service.post(`/patients/${patientId}/priority_notes`, data),
        id: (priorityNoteId) => ({
          update: (data) => Service.patch(`/patients/${patientId}/priority_notes/${priorityNoteId}`, data),
          setTopPriority: (topPriority) =>
            Service.patch(`/patients/${patientId}/priority_notes/${priorityNoteId}/top_priority`, { top_priority: topPriority }),
          archive: () => Service.patch(`/patients/${patientId}/priority_notes/${priorityNoteId}/archive`),
        }),
      },
      tasks: {
        list: (query) => Service.get(`/patients/${patientId}/tasks`, query),
        id: (taskId) => ({
          details: () => Service.get(`/patients/${patientId}/tasks/${taskId}`),
          notes: {
            create: (data) => Service.post(`/patients/${patientId}/tasks/${taskId}/notes`, data),
            list: (query) => Service.get(`/patients/${patientId}/tasks/${taskId}/notes`, query),
          },
        }),
      },
      hivLabs: {
        id: (labId) => ({
          submit: (data) => Service.patch(`/patients/${patientId}/hiv_labs/${labId}`, data),
          decline: () => Service.delete(`/patients/${patientId}/hiv_labs/${labId}`),
        }),
      },
      insurancePartners: {
        update: (data) => Service.post(`/patients/${patientId}/cbo_insurance_partners`, data),
        invite: () => Service.post(`/patients/${patientId}/cbo_insurance_partners/send_invite`),
      },
      encounters: {
        create: (data) => Service.post(`/patients/${patientId}/encounters`, data),
        list: (query) => Service.get(`/patients/${patientId}/encounters`, query),
        available: (query) => Service.get(`/patients/${patientId}/encounter_types`, query),
        id: (encounterId) => ({
          details: () => Service.get(`/patients/${patientId}/encounters/${encounterId}`),
          update: (data) => Service.patch(`/patients/${patientId}/encounters/${encounterId}`, data),
          remove: () => Service.delete(`/patients/${patientId}/encounters/${encounterId}`),
          notes: {
            create: (data) => Service.post(`/patients/${patientId}/encounters/${encounterId}/notes`, data),
            list: (query) => Service.get(`/patients/${patientId}/encounters/${encounterId}/notes`, query),
          },
          appointments: {
            list: (query) => Service.get(`/patients/${patientId}/encounters/${encounterId}/appointments`, query),
            create: (data) => Service.post(`/patients/${patientId}/encounters/${encounterId}/appointments`, data),
            scheduled: {
              create: (data) => Service.post(`/patients/${patientId}/encounters/${encounterId}/scheduled_appointments`, data),
              id: (appointmentId) => ({
                update: (data) =>
                  Service.patch(`/patients/${patientId}/encounters/${encounterId}/scheduled_appointments/${appointmentId}`, data),
              }),
            },
            id: (appointmentId) => ({
              submitSoap: (data) => Service.patch(`/patients/${patientId}/encounters/${encounterId}/appointments/${appointmentId}`, data),
            }),
          },
          availabilities: {
            list: (query) => Service.get(`/patients/${patientId}/encounters/${encounterId}/availabilities`, query),
          },
          labs: {
            list: (query) => Service.get(`/patients/${patientId}/encounters/${encounterId}/labs`, query),
          },
          prescriptions: {
            list: (query) => Service.get(`/patients/${patientId}/encounters/${encounterId}/prescriptions`, query),
          },
          vaccines: {
            list: (query) => Service.get(`/patients/${patientId}/encounters/${encounterId}/vaccine_orders`, query),
            create: (data) => Service.post(`/patients/${patientId}/encounters/${encounterId}/vaccine_orders`, data),
          },
          assessments: {
            list: (query) => Service.get(`/patients/${patientId}/encounters/${encounterId}/assessments`, query),
            create: (data) => Service.post(`/patients/${patientId}/encounters/${encounterId}/assessments`, data),
            id: (assessmentId) => ({
              review: () => Service.post(`/patients/${patientId}/encounters/${encounterId}/assessments/${assessmentId}/review`),
              resend: () => Service.put(`/patients/${patientId}/encounters/${encounterId}/assessments/${assessmentId}/resend`),
            }),
          },
          referrals: {
            list: (query) => Service.get(`/patients/${patientId}/encounters/${encounterId}/referrals`, query),
            create: (data) => Service.post(`/patients/${patientId}/encounters/${encounterId}/referrals`, data),
            preview: (query) => Service.get(`/patients/${patientId}/encounters/${encounterId}/referrals/preview`, query, fileConfig),
          },
          morbidities: {
            list: (query) => Service.get(`/patients/${patientId}/encounters/${encounterId}/doh_morbidities`, query),
            create: (data) => Service.post(`/patients/${patientId}/encounters/${encounterId}/doh_morbidities`, data),
            preview: (query) => Service.get(`/patients/${patientId}/encounters/${encounterId}/doh_morbidities/preview`, query, fileConfig),
          },
        }),
      },
    }),
  },
  appointments: {
    list: (query) => Service.get('/appointments', query),
    reports: {
      encounter: (query) => Service.get('/appointments/reports/encounter', query),
      incompleteQhaEncounters: (query) => Service.get('/appointments/reports/incomplete_qha_encounters', query),
      flaggedQhaEncounters: (query) => Service.get('/appointments/reports/flagged_qha_encounters', query),
      stiTesting: (query) => Service.get('/appointments/reports/sti_testing', query),
      appointmentMonthly: (query) => Service.get('/appointments/reports/appointment_monthly', query),
      appointmentConflicts: () => Service.get('/appointments/reports/appointment_conflicts'),
      encounterMissingRx: (query) => Service.get('/appointments/reports/encounter_missing_rx', query),
      onDemand: (query) => Service.get('/appointments/reports/on_demand', query),
      availability: (query) => Service.get('/appointments/reports/availability', query),
      scheduledToCompletion: (query) => Service.get('/appointments/reports/scheduled_to_completion', query),
      adHoc: (query) => Service.get('/appointments/reports/ad_hoc', query),
    },
    onDemand: {
      list: (query) => Service.get('/on_demand_appointments', query),
      create: (data) => Service.post('/on_demand_appointments', data),
      id: (onDemandLogId) => ({
        details: (config) => Service.get(`/on_demand_appointments/${onDemandLogId}`, undefined, config),
        accept: () => Service.patch(`/on_demand_appointments/${onDemandLogId}`),
        heartbeat: (config) => Service.patch(`/on_demand_appointments/${onDemandLogId}/heartbeat`, undefined, config),
      }),
    },
    asyncMessaging: {
      list: (query) => Service.get('/async_messaging_appointments', query),
      create: (data) => Service.post('/async_messaging_appointments', data),
      id: (appointmentId) => ({
        details: (query, config) => Service.get(`/async_messaging_appointments/${appointmentId}`, query, config),
        accept: () => Service.patch(`/async_messaging_appointments/${appointmentId}/accept`),
        schedule: (data) => Service.post(`/async_messaging_appointments/${appointmentId}/schedule`, data),
        onDemand: (data) => Service.post(`/async_messaging_appointments/${appointmentId}/on_demand`, data),
        complete: (data) => Service.patch(`/async_messaging_appointments/${appointmentId}`, data),
        cancel: () => Service.delete(`/async_messaging_appointments/${appointmentId}`),
        workflow: (data) => Service.patch(`/async_messaging_appointments/${appointmentId}/workflow`, data),
      }),
    },
    id: (appointmentId) => ({
      details: (query, config) => Service.get(`/appointments/${appointmentId}`, query, config),
      export: (query) => Service.get(`/appointments/${appointmentId}/show_soap_note`, query, fileConfig),
      soap: () => Service.get(`/appointments/${appointmentId}/soap_note`),
      avs: () => Service.get(`/appointments/${appointmentId}/after_visit_summary`),
      labs: (query) => Service.get(`/appointments/${appointmentId}/labs`, query),
      prescriptions: (query) => Service.get(`/appointments/${appointmentId}/prescriptions`, query),
      videoToken: (query) => Service.get(`/appointments/${appointmentId}/video_token`, query),
      markAsMissed: () => Service.post(`/appointments/${appointmentId}/set_missed`),
      markAsCanceled: () => Service.post(`/appointments/${appointmentId}/set_canceled`),
      markAsFailed: (data) => Service.post(`/appointments/${appointmentId}/fail`, data),
      asyncAssessments: (query) => Service.get(`/appointments/${appointmentId}/async_soap_assessments`, query),
      notes: {
        list: (query) => Service.get(`/appointments/${appointmentId}/notes`, query),
        create: (data) => Service.post(`/appointments/${appointmentId}/notes`, data),
      },
    }),
  },
  scheduleSlots: {
    declined: (query) => Service.get('/schedule_slots/declined', query),
    logs: (query) => Service.get('/schedule_slots/logs', query),
    providers: {
      list: (query) => Service.get('/schedule_slots/providers', query),
      scheduled: {
        on: (query) => Service.get('/schedule_slots/providers/scheduled_on', query),
        off: (query) => Service.get('/schedule_slots/providers/scheduled_off', query),
        ooo: (query) => Service.get('/schedule_slots/providers/out_of_office', query),
      },
      unassignedQhaLifestyleMeds: (query) => Service.get('/schedule_slots/providers/unassigned_qha_lifestyle_meds', query),
      id: (providerId) => ({
        pending: (query) => Service.get(`/schedule_slots/providers/${providerId}/pending`, query),
      }),
    },
    users: {
      scheduled: {
        on: (query) => Service.get('/schedule_slots/users/scheduled_on', query),
        off: (query) => Service.get('/schedule_slots/users/scheduled_off', query),
        ooo: (query) => Service.get('/schedule_slots/users/out_of_office', query),
      },
    },
  },
  availabilities: {
    id: (availabilityId) => ({
      approve: () => Service.put(`/availabilities/${availabilityId}/authorizations`),
      decline: () => Service.delete(`/availabilities/${availabilityId}/authorizations`),
    }),
  },
  gapBoard: {
    providers: (query) => Service.get('/gap_board/providers', query).then(deepCamelizeKeys),
  },
  gapRequests: {
    list: (query) => Service.get('/gap_requests', query),
    create: (data) => Service.post('/gap_requests', data),
    id: (gapRequestId) => ({
      update: (data) => Service.patch(`/gap_requests/${gapRequestId}`, data),
      remove: () => Service.delete(`/gap_requests/${gapRequestId}`),
    }),
  },
  providers: {
    list: (query) => Service.get('/providers', query),
    availabilitySummary: (query) => Service.get('/providers/availabilities_summary', query),
    availabilitySummaryCSV: (query) => Service.get('/providers/availabilities_summary_csv', query, fileConfig),
    importAvailabilities: (data) => Service.post('/providers/import_availabilities', data, { skipDeserialization: true }),
    reports: {
      licenses: (query) => Service.get('/providers/reports/licenses', query),
      licensesOverview: () => Service.get('/providers/reports/licenses/overview').then(deepCamelizeKeys),
      licensesProviders: (query) => Service.get('/providers/reports/licenses/providers', query).then(deepCamelizeKeys),
      licensesMedicaidGaps: () => Service.get('/providers/reports/licenses/medicaid_gaps').then(deepCamelizeKeys),
      doh: (query) => Service.get('/providers/reports/doh', query),
      sti: (query) => Service.get('/providers/reports/sti', query),
      roster: (query) => Service.get('/providers/reports/roster', query),
      productivity: (query) => Service.get('/providers/reports/productivity', query),
      audit: () => Service.get('/providers/reports/audit'),
      reminderTasks: (query) => Service.get('/providers/reports/reminder_tasks', query),
    },
    assignments: (query) =>
      Service.get('/providers/dashboard_provider_assignments', query).then((response) => {
        return response.data.map((item) => item.attributes)
      }),
    activeCensus: (query) =>
      Service.get('/providers/dashboard_provider_census', query).then((response) => {
        return response.data.map((item) => item.attributes)
      }),
    ranks: (query) => Service.get('/providers/ranks', query),
    availabilityGaps: {
      daily: (query) => Service.get(`/providers/availability_gaps`, query).then(mapGaps),
      weekly: (query) => Service.get(`/providers/availability_gaps/weekly`, query).then(mapWeeklyGaps),
    },
    id: (providerId) => ({
      details: (config) => Service.get(`/providers/${providerId}`, undefined, config),
      metrics: (query) => ({
        data: () => Service.get(`/providers/${providerId}/metrics`, query),
        export: () => Service.get(`/providers/${providerId}/metrics/csv`, query, fileConfig),
      }),
      schedulingWarning: () => Service.post(`/providers/${providerId}/scheduled_hours_notification`),
      licenses: {
        list: (query) => Service.get(`/providers/${providerId}/licenses`, query),
        create: (data) => Service.post(`/providers/${providerId}/licenses`, data),
        id: (licenseId) => ({
          details: () => Service.get(`/providers/${providerId}/licenses/${licenseId}`),
          update: (data) => Service.put(`/providers/${providerId}/licenses/${licenseId}`, data),
          remove: () => Service.delete(`/providers/${providerId}/licenses/${licenseId}`),
          notes: {
            list: (query) => Service.get(`/providers/${providerId}/licenses/${licenseId}/notes`, query),
            create: (data) => Service.post(`/providers/${providerId}/licenses/${licenseId}/notes`, data),
          },
        }),
      },
      appointments: {
        list: (query) => Service.get(`/providers/${providerId}/appointments`, query),
        dashboard: (query, config) => Service.get(`/providers/${providerId}/dashboard_next_appointments`, query, config),
        id: (appointmentId) => ({
          start: () => Service.post(`/providers/${providerId}/appointments/${appointmentId}/start`),
          markAsCompleted: () => Service.post(`/providers/${providerId}/appointments/${appointmentId}/complete`),
          completeCall: () => Service.post(`/providers/${providerId}/appointments/${appointmentId}/complete_call`),
          createSwapType: (data) => Service.post(`/providers/${providerId}/appointments/${appointmentId}/create_swap_type`, data),
        }),
        adHoc: {
          list: (query) => Service.get(`/providers/${providerId}/ad_hoc_appointments`, query),
          create: (data) => Service.post(`/providers/${providerId}/ad_hoc_appointments`, data),
          id: (appointmentId) => ({
            update: (data) => Service.patch(`/providers/${providerId}/ad_hoc_appointments/${appointmentId}`, data),
            remove: () => Service.delete(`/providers/${providerId}/ad_hoc_appointments/${appointmentId}`),
            notes: {
              list: (query) => Service.get(`/providers/${providerId}/ad_hoc_appointments/${appointmentId}/notes`, query),
              create: (data) => Service.post(`/providers/${providerId}/ad_hoc_appointments/${appointmentId}/notes`, data),
            },
          }),
        },
      },
      encounters: {
        completed: (query) => Service.get(`/providers/${providerId}/completed_encounters`, query),
      },
      labs: {
        dashboard: (query) => Service.get(`/providers/${providerId}/dashboard_labs_review`, query).then(mapDashboardLabs),
      },
      assessments: {
        dashboard: (query, config) => Service.get(`/providers/${providerId}/dashboard_assessments_review`, query, config),
      },
      par: {
        list: (query) => Service.get(`/providers/${providerId}/par_tasks`, query),
        id: (taskId) => ({
          complete: (data) => Service.post(`/providers/${providerId}/par_tasks/${taskId}/mark_completed`, data),
        }),
      },
      licenseTasks: {
        list: (query) => Service.get(`/providers/${providerId}/license_tasks`, query),
        id: (taskId) => ({
          complete: (data) => Service.post(`/providers/${providerId}/license_tasks/${taskId}/mark_completed`, data),
        }),
      },
      cbos: {
        list: (query, config) => Service.get(`/providers/${providerId}/cbos`, query, config),
      },
      prescriptions: {
        url: (data) => Service.post(`/providers/${providerId}/prescriptions/rx_url`, data),
        sync: (data) => Service.post(`/providers/${providerId}/prescriptions/sync`, data),
      },
      availabilities: {
        list: (query) => Service.get(`/providers/${providerId}/availabilities`, query),
        monthly: (query) =>
          Service.get(`/providers/${providerId}/availabilities/monthly_count`, query).then((data) =>
            data.reduce((acc, day) => {
              Object.keys(day).forEach((date) => (acc[date] = day[date]))
              return acc
            }, {})
          ),
        create: (data) => Service.post(`/providers/${providerId}/availabilities`, data),
        createRange: (data) => Service.post(`/providers/${providerId}/availabilities/range`, data),
        id: (availabilityId) => ({
          update: (data) => Service.patch(`/providers/${providerId}/availabilities/${availabilityId}`, data),
          remove: () => Service.delete(`/providers/${providerId}/availabilities/${availabilityId}`),
        }),
      },
    }),
  },
  cbos: {
    list: (query, config) => Service.get('/cbos', query, config),
    coverage: () => Service.get('/cbos_map_coverages'),
    report: () => Service.get('/cbos/report'),
    id: (cboId) => ({
      details: (query, config) => Service.get(`/cbos/${cboId}`, query, config),
      coverage: {
        list: (query) => Service.get(`/cbos/${cboId}/coverage`, query),
        create: (data) => Service.post(`/cbos/${cboId}/coverage`, data),
        id: (coverageId) => ({
          update: (data) => Service.patch(`/cbos/${cboId}/coverage/${coverageId}`, data),
          remove: () => Service.delete(`/cbos/${cboId}/coverage/${coverageId}`),
        }),
      },
      notes: {
        list: (query) => Service.get(`/cbos/${cboId}/notes`, query),
        create: (data) => Service.post(`/cbos/${cboId}/notes`, data),
      },
      prescriptionFileSettings: {
        list: (query) => Service.get(`/cbos/${cboId}/cbo_prescription_file_settings`, query),
        create: (data) => Service.post(`/cbos/${cboId}/cbo_prescription_file_settings`, data),
        id: (settingId) => ({
          update: (data) => Service.patch(`/cbos/${cboId}/cbo_prescription_file_settings/${settingId}`, data),
          remove: () => Service.delete(`/cbos/${cboId}/cbo_prescription_file_settings/${settingId}`),
        }),
      },
      metrics: (query) => ({
        data: () => Service.get(`/cbos/${cboId}/metrics`, query),
        export: () => Service.get(`/cbos/${cboId}/metrics/csv`, query, fileConfig),
      }),
      providers: {
        list: (query) => Service.get(`/cbos/${cboId}/providers`, query),
        ranks: (query) => Service.get(`/cbos/${cboId}/providers/ranks`, query),
        possibleDefault: (query) => Service.get(`/cbos/${cboId}/possible_default_providers`, query),
        gaps: {
          availability: (query) => Service.get(`/cbos/${cboId}/providers/availability_gaps`, query).then(mapGaps),
          weekly: (query) => Service.get(`/cbos/${cboId}/providers/weekly_gaps`, query),
        },
      },
      patients: {
        former: {
          list: (query) => Service.get(`/cbos/${cboId}/former_patients`, query),
          id: (patientId) => ({
            details: () => Service.get(`/cbos/${cboId}/former_patients/${patientId}`),
            encounters: {
              list: (query) => Service.get(`/cbos/${cboId}/former_patients/${patientId}/encounters`, query),
            },
          }),
        },
      },
      ruleSets: {
        list: (query, config) => Service.get(`/cbos/${cboId}/rule_sets`, query, config).then(mapRuleSets),
        update: (data) => Service.patch(`/cbos/${cboId}/rule_sets`, data).then(mapRuleSets),
      },
      priority: {
        add: (data) => Service.patch(`/cbos/${cboId}/priority_by_state`, data),
        remove: (data) => Service.delete(`/cbos/${cboId}/priority_by_state`, data),
      },
      pharmacies: {
        contracted: {
          list: (query) => Service.get(`/cbos/${cboId}/contracted_pharmacies`, query),
        },
      },
    }),
  },
  conversations: {
    list: (query, config) => Service.get('/conversations', query, config),
    create: (data, config) => Service.post('/conversations', data, config),
    search: (query, config) => Service.get('/conversations/search', query, config),
    id: (conversationId) => ({
      details: (query) => Service.get(`/conversations/${conversationId}`, query),
      pin: () => Service.post(`/conversations/${conversationId}/pin`),
      unpin: () => Service.post(`/conversations/${conversationId}/unpin`),
      archive: () => Service.post(`/conversations/${conversationId}/archive`),
      unarchive: () => Service.post(`/conversations/${conversationId}/unarchive`),
      notes: {
        create: (data) => Service.post(`/conversations/${conversationId}/notes`, data),
        list: (query) => Service.get(`/conversations/${conversationId}/notes`, query),
      },
      messages: {
        list: (query) => Service.get(`/conversations/${conversationId}/messages`, query),
        send: (data, config) => Service.patch(`/conversations/${conversationId}`, data, config),
        id: (messageId) => ({
          details: (config) => Service.get(`/conversations/${conversationId}/messages/${messageId}`, undefined, config),
          delete: (config) => Service.delete(`/conversations/${conversationId}/messages/${messageId}`, undefined, config),
        }),
      },
      users: {
        add: (data) => Service.post(`/conversations/${conversationId}/users`, data),
        behalf: (query, config) => Service.get(`/conversations/${conversationId}/send_on_behalf_of_users`, query, config),
        search: (query) => Service.get(`/conversations/${conversationId}/users/search`, query),
        id: (userId) => ({
          remove: () => Service.delete(`/conversations/${conversationId}/users/${userId}`),
        }),
      },
    }),
  },
  pharmacies: {
    list: (query) => Service.get('/pharmacies', query),
    create: (data) => Service.post('/pharmacies', data),
    reports: {
      patientAssignment: (query) => Service.get('/pharmacies/reports/patient_assignment', query),
      unrepliedChats: (query) => Service.get('/pharmacies/reports/unreplied_chats', query),
    },
    id: (pharmacyId) => ({
      details: () => Service.get(`/pharmacies/${pharmacyId}`),
      update: (data) => Service.patch(`/pharmacies/${pharmacyId}`, data),
      remove: () => Service.delete(`/pharmacies/${pharmacyId}`),
      updateIndicators: (data) => Service.patch(`/pharmacies/${pharmacyId}/update_24h_and_pep`, data),
      updateAvita: (data) => Service.patch(`/pharmacies/${pharmacyId}/update_avita`, data),
    }),
    tasks: {
      list: (query) => Service.get('/pharmacies/tasks', query),
      id: (taskId) => ({
        details: (query, config) => Service.get(`/pharmacies/tasks/${taskId}`, query, config),
        return: (data) => Service.post(`/pharmacies/tasks/${taskId}/mark_returned_pharmacy`, data),
      }),
    },
  },
  labs: {
    list: (query) => Service.get('/labs', query),
    export: (query) => Service.get('/labs/report', query),
    id: (labId) => ({
      details: () => Service.get(`/labs/${labId}`),
      update: (data, config) => Service.patch(`/labs/${labId}`, data, config),
      remove: () => Service.delete(`/labs/${labId}`),
      resubmit: () => Service.patch(`/labs/${labId}/resubmit`),
      canReview: () => Service.get(`/labs/${labId}/can_review`),
      notes: {
        list: (query) => Service.get(`/labs/${labId}/notes`, query),
        create: (data) => Service.post(`/labs/${labId}/notes`, data),
      },
    }),
    unmatched: {
      list: (query) => Service.get('/unmatched_labs', query),
      id: (labId) => ({
        details: () => Service.get(`/unmatched_labs/${labId}`),
        update: (data) => Service.patch(`/unmatched_labs/${labId}`, data),
        remove: () => Service.delete(`/unmatched_labs/${labId}`),
      }),
    },
  },
  prescriptions: {
    list: (query) => Service.get('/prescriptions', query),
    export: (query) => Service.get('/prescriptions/report', query),
    id: (prescriptionId) => ({
      details: () => Service.get(`/prescriptions/${prescriptionId}`),
      update: (data) => Service.patch(`/prescriptions/${prescriptionId}`, data),
      remove: () => Service.delete(`/prescriptions/${prescriptionId}`),
      notes: {
        list: (query) => Service.get(`/prescriptions/${prescriptionId}/notes`, query),
        create: (data) => Service.post(`/prescriptions/${prescriptionId}/notes`, data),
      },
    }),
  },
  assessments: {
    list: (query) => Service.get('/assessments', query),
    pending: {
      list: (query) => Service.get('/assessments/pending_health_assessments', query),
    },
    id: (assessmentId) => ({
      details: () => Service.get(`/assessments/${assessmentId}`),
      markExceptional: (data) => Service.patch(`/assessments/${assessmentId}/mark_exceptional`, data),
      unmarkExceptional: () => Service.patch(`/assessments/${assessmentId}/unmark_exceptional`),
      notes: {
        list: (query) => Service.get(`/assessments/${assessmentId}/notes`, query),
        create: (data) => Service.post(`/assessments/${assessmentId}/notes`, data),
      },
    }),
  },
  labRequests: {
    list: (query) => Service.get('/lab_requests', query),
    id: (labRequestId) => ({
      details: () => Service.get(`/lab_requests/${labRequestId}`),
    }),
  },
  lhcDashboard: {
    list: (query) => Service.get('/lhc_dashboard', query),
    id: (summaryId) => ({
      details: () => Service.get(`/lhc_dashboard/${summaryId}`),
      notes: {
        list: (query) => Service.get(`/lhc_dashboard/${summaryId}/notes`, query),
        create: (data) => Service.post(`/lhc_dashboard/${summaryId}/notes`, data),
      },
    }),
  },
  users: {
    list: (query) => Service.get('/users', query),
    id: (userId) => ({
      details: () => Service.get(`/users/${userId}`),
      update: (data) => Service.patch(`/users/${userId}`, data),
      metrics: (query) => ({
        data: () => Service.get(`/users/${userId}/metrics`, query),
        export: () => Service.get(`/users/${userId}/metrics/csv`, query, fileConfig),
      }),
      indicators: (type, query) => Service.get(`/users/${userId}/indicators/${type}`, query),
      card: (config) => Service.get(`/users/${userId}/info_card`, undefined, config).then(mapUserCard),
      sendMessage: (data, config) => Service.post(`/users/${userId}/conversations`, data, config),
      sendCSMessage: (data) => Service.post(`/users/${userId}/support_conversations`, data),
      sendReminder: (data) => Service.post(`/users/${userId}/message_reminders`, data),
      preferences: {
        optIn: () => Service.post(`/users/${userId}/preferences/opt_in`),
        optOut: () => Service.post(`/users/${userId}/preferences/opt_out`),
      },
      password: {
        reset: () => Service.post(`/users/${userId}/reset_password`),
        update: (data, config) => Service.patch(`/users/${userId}/password`, data, config),
      },
      pharmacies: {
        list: (query) => Service.get(`/users/${userId}/pharmacies`, query),
        id: (pharmacyId) => ({
          update: (data) => Service.patch(`/users/${userId}/pharmacies/${pharmacyId}`, data),
        }),
      },
      photo: {
        update: (data, config) => Service.post(`/users/${userId}/profile_picture`, data, config),
        remove: () => Service.delete(`/users/${userId}/profile_picture`),
      },
      status: {
        activate: (data, config) => Service.patch(`/user_status/${userId}/activate`, data, config),
        deactivate: (data, config) => Service.patch(`/user_status/${userId}/deactivate`, data, config),
        disable: (data, config) => Service.patch(`/user_status/${userId}/disable`, data, config),
        unlock: (data, config) => Service.patch(`/user_status/${userId}/unlock`, data, config),
      },
      mentions: {
        list: (query) => Service.get(`/users/${userId}/message_mentions`, query),
      },
      adminTimes: {
        list: (query) => Service.get(`/users/${userId}/admin_times`, query),
        create: (data) => Service.post(`/users/${userId}/admin_times`, data),
        id: (timeId) => ({
          update: (data) => Service.patch(`/users/${userId}/admin_times/${timeId}`, data),
          remove: () => Service.delete(`/users/${userId}/admin_times/${timeId}`),
          approve: () => Service.put(`/users/${userId}/admin_times/${timeId}/authorizations`),
          decline: () => Service.delete(`/users/${userId}/admin_times/${timeId}/authorizations`),
        }),
      },
      outOfOfficeTimes: {
        list: (query) => Service.get(`/users/${userId}/out_of_office_times`, query),
        create: (data) => Service.post(`/users/${userId}/out_of_office_times`, data),
        id: (timeId) => ({
          update: (data) => Service.patch(`/users/${userId}/out_of_office_times/${timeId}`, data),
          remove: () => Service.delete(`/users/${userId}/out_of_office_times/${timeId}`),
          approve: () => Service.put(`/users/${userId}/out_of_office_times/${timeId}/authorizations`),
          decline: () => Service.delete(`/users/${userId}/out_of_office_times/${timeId}/authorizations`),
          notes: {
            list: (query) => Service.get(`/users/${userId}/out_of_office_times/${timeId}/notes`, query),
            create: (data) => Service.post(`/users/${userId}/out_of_office_times/${timeId}/notes`, data),
          },
        }),
      },
      notifications: {
        list: (query) => Service.get(`/users/${userId}/user_notifications`, query),
        read: () => Service.post(`/users/${userId}/user_notifications/mark_all_read`),
        unread: () => Service.patch(`/users/${userId}/user_notifications/mark_all_unread`),
        id: (notificationId) => ({
          update: (data) => Service.patch(`/users/${userId}/user_notifications/${notificationId}`, data),
          mark: {
            read: () => Service.patch(`/users/${userId}/user_notifications/${notificationId}/mark_read`),
            unread: () => Service.patch(`/users/${userId}/user_notifications/${notificationId}/mark_unread`),
          },
        }),
      },
      sopNotifications: {
        list: (query) => Service.get(`/users/${userId}/sop_notifications`, query),
        id: (notificationId) => ({
          read: () => Service.patch(`/users/${userId}/sop_notifications/${notificationId}`),
        }),
      },
      settings: {
        lists: (config) => Service.get(`/users/${userId}/user_settings`, undefined, config),
        id: (settingId) => ({
          details: (config) => Service.get(`/users/${userId}/user_settings/${settingId}`, undefined, config),
          update: (data, config) => Service.patch(`/users/${userId}/user_settings/${settingId}`, data, config),
          remove: (config) => Service.delete(`/users/${userId}/user_settings/${settingId}`, undefined, config),
        }),
      },
      tours: {
        list: (query, config) => Service.get(`/users/${userId}/tours`, query, config).then(mapTours),
        id: (tourId) => ({
          details: (config) => Service.get(`/users/${userId}/tours/${tourId}`, undefined, config),
          update: (data) => Service.put(`/users/${userId}/tours/${tourId}`, data),
        }),
      },
      webPushSubscriptions: {
        create: (data) => Service.post(`/users/${userId}/web_push_subscriptions`, data),
      },
    }),
  },
  systemUsers: {
    list: (query) => Service.get('/system_users', query),
    reports: {
      deviceInfo: (query) => Service.get('/system_users/reports', query),
      unrepliedChats: (query) => Service.get('/system_users/reports/unreplied_chats', query),
      unrepliedMessages: (query) => Service.get('/system_users/reports/unreplied_messages', query),
    },
    id: (userId) => ({
      details: () => Service.get(`/system_users/${userId}`),
    }),
  },
  calls: {
    list: (query) => Service.get('/phone_calls', query),
    token: (query) => Service.get('/phone_calls/phone_token', query),
  },
  snippets: {
    list: (query) => Service.get('/snippets', query),
    create: (data, config) => Service.post('/snippets', data, config),
    id: (snippetId) => ({
      details: () => Service.get(`/snippets/${snippetId}`),
      update: (data, config) => Service.patch(`/snippets/${snippetId}`, data, config),
      setStatus: (data) => Service.patch(`/snippets/${snippetId}/set_status`, data),
    }),
  },
  sops: {
    list: (query) => Service.get('/sops', query),
    create: (data, config) => Service.post('/sops', data, config),
    id: (sopId) => ({
      details: () => Service.get(`/sops/${sopId}`),
      update: (data, config) => Service.patch(`/sops/${sopId}`, data, config),
      remove: () => Service.delete(`/sops/${sopId}`),
      notifications: {
        create: (data) => Service.post(`/sops/${sopId}/sop_notifications`, data),
      },
    }),
  },

  reports: {
    breakGlass: (query) => Service.get('/reports/break_glass', query),
    gilead: (query) => Service.get('/reports/gilead', query),
    gileadDetail: (query) => Service.get('/reports/gilead_detail', query),
    roleLogin: (query) => Service.get('/reports/role_login', query),
    providerAssignment: (query) => Service.get('/reports/provider_assignment', query),
    sopNotifications: (query) => Service.get('/reports/sop_notifications', query),
    maProductivity: (query) => Service.get('/reports/ma_productivity', query),
    maRoster: (query) => Service.get('/reports/ma_roster', query),
    supportRoster: (query) => Service.get('/reports/support_roster', query),
    supportProductivity: (query) => Service.get('/reports/support_productivity', query),
    quarterlyLab: (query) => Service.get('/reports/quarterly_lab', query),
    quarterlyAssessment: (query) => Service.get('/reports/quarterly_assessment', query),
    taskEscalation: (query) => Service.get('/reports/task_escalation', query),
    activePatientsVsPrescriptions: (query) => Service.get('/admin/reports/active_patients_vs_prescriptions', query),
    enrolledVsActivePatients: (query) => Service.get('/admin/reports/enrolled_active_patients', query),
    cbosWeeklyKpis: (query) => Service.get('/admin/reports/cbos_weekly_kpis', query),
    stateWeeklyKpis: (query) => Service.get('/admin/reports/state_weekly_kpis', query),
    completedReschedulesFromNoShows: (query) => Service.get('/admin/reports/completed_reschedules_from_no_shows', query),
    enrolledApptCompleted: (query) => Service.get('/admin/reports/enrolled_appt_completed', query),
    enrollmentsAndServiceLineScheduled: (query) => Service.get('/admin/reports/enrollments_and_service_line_scheduled', query),
    initialEncounters: (query) => Service.get('/admin/reports/initial_encounters', query),
    prepInitialScheduledFromDailyEnrollment: (query) => Service.get('/admin/reports/prep_initial_scheduled_from_daily_enrollment', query),
    newVsTransferPrep: () => Service.get('/admin/reports/new_vs_existing_prep'),
    prepInitialNoShows: () => Service.get('/admin/reports/prep_initial_no_shows'),
    userJourneys: {
      overview: (query) => Service.get('/admin/reports/user_journeys/overview', query),
      funnel: (query) => Service.get('/admin/reports/user_journeys/funnel', query),
      fieldDropOffs: (query) => Service.get('/admin/reports/user_journeys/field_drop_offs', query),
      unopenedVisits: (query) => Service.get('/admin/reports/user_journeys/unopened_visits', query),
      steps: (query) => Service.get('/admin/reports/user_journeys/steps', query),
      journeys: (query) => Service.get('/admin/reports/user_journeys/journeys', query),
    },
  },

  reportLogs: {
    list: (query) => Service.get('/reports', query),
    opened: (data) => Service.post('/reports/opened', data),
  },

  notificationLogs: {
    list: (query) => Service.get('/notifications', query),
    csv: (query) => Service.get('/notifications/csv', query, fileConfig),
  },

  dohFaxNumbers: {
    list: () => Service.get('/doh_fax_numbers'),
    create: (data) => Service.post('/doh_fax_numbers', data),
    id: (dohFaxNumberId) => ({
      update: (data) => Service.patch(`/doh_fax_numbers/${dohFaxNumberId}`, data),
      remove: () => Service.delete(`/doh_fax_numbers/${dohFaxNumberId}`),
    }),
  },

  tasks: {
    list: (query) => Service.get('/tasks', query),
    create: (data) => Service.post('/tasks', data),
    exportBacklog: (query) => Service.get('/tasks/not_completed_csv', query, fileConfig),
    id: (taskId) => ({
      details: () => Service.get(`/tasks/${taskId}`),
      updateAssignee: (data) => Service.post(`/tasks/${taskId}/update_assignee`, data),
      updatePriority: (data) => Service.post(`/tasks/${taskId}/update_priority`, data),
      mark: {
        todo: () => Service.post(`/tasks/${taskId}/mark_todo`),
        inProgress: () => Service.post(`/tasks/${taskId}/mark_in_progress`),
        waiting: () => Service.post(`/tasks/${taskId}/mark_waiting`),
        completed: (data) => Service.post(`/tasks/${taskId}/mark_completed`, data),
        notApplicable: (data) => Service.post(`/tasks/${taskId}/mark_not_applicable`, data),
        expired: (data) => Service.post(`/tasks/${taskId}/mark_expired`, data),
      },
      notes: {
        list: (query) => Service.get(`/tasks/${taskId}/notes`, query),
        create: (data) => Service.post(`/tasks/${taskId}/notes`, data),
      },
      documents: {
        list: (query) => Service.get(`/tasks/${taskId}/documents`, query).then(mapTaskFiles),
        attach: (data) => Service.post(`/tasks/${taskId}/documents`, data),
        id: (fileId) => ({
          remove: () => Service.delete(`/tasks/${taskId}/documents/${fileId}`),
        }),
      },
    }),
  },
  escalationTasks: {
    list: (query) => Service.get('/escalation_tasks', query),
    create: (data) => Service.post('/escalation_tasks', data),
    checkDuplicates: (query) => Service.get('/escalation_tasks/check_duplicates', query),
    id: (taskId) => ({
      details: () => Service.get(`/escalation_tasks/${taskId}`),
    }),
  },
  tours: {
    list: (query, config) => Service.get('/tour_guides', query, config),
    create: (data) => Service.post('/tour_guides', data),
    id: (tourId) => ({
      update: (data) => Service.patch(`/tour_guides/${tourId}`, data),
    }),
  },
  userJourneys: {
    track: (data, config) => Service.post('/user_journeys/track', data, config),
  },
}

export default API
